<template>
	<div class="date-picker-wrapper">
		<!-- Label -->
		 <template v-if="label">
			<label
				v-show="showLabel"
				:id="labelId"
				:for="inputId"
				class="date-picker-label"
			>
				{{ label }}
				<span v-if="required" class="required-asterisk">*</span>
			</label>
		</template>

		<!-- Input + trigger button -->
		<div
			class="input-group"
			v-if="validateInput"
		>
			<validation-provider
				:name="validationName"
				:rules="validationInputRules"
			>
				<div slot-scope="{ errors }">
					<input
						:id="inputId"
						ref="input"
						type="text"
						class="form-control date-input"
						:name="name"
						:value="displayValue"
						:placeholder="placeholder"
						:disabled="disabled"
						:required="required"
						:readonly="!editable"
						:aria-labelledby="label ? labelId : undefined"
						:aria-describedby="errorMessage ? errorMessage : undefined"
						:aria-invalid="errorMessage ? 'true' : undefined"
						@click="handleInputClick"
						@dblclick="setToday"
						@blur="handleBlur"
						@input="handleInput"
						@change="handleChange"
						@keydown.space.prevent="openCalendar"
						@keydown.enter.prevent="openCalendar"
					/>

					<span :id="describedByID" v-show="false">
						{{ datePickerInputMessage }}
					</span>

					<div class="input-group-append" style="white-space:nowrap;">
						<button
							type="button"
							class="btn btn-outline-secondary calendar-btn"
							:aria-expanded="isOpen ? 'true' : 'false'"
							aria-haspopup="dialog"
							:aria-label="triggerAriaLabel"
							:disabled="disabled"
							@click="toggleCalendar"
						>
							<i class="fa fa-calendar" aria-hidden="true"></i>
						</button>
					</div>
					<small class="text-danger">{{ errors[0] }}</small>
				</div>
			</validation-provider>
		</div>

		<div
			class="input-group"
			v-else
		>
			<input
				:id="inputId"
				ref="input"
				type="text"
				class="form-control date-input"
				:name="name"
				:value="displayValue"
				:placeholder="placeholder"
				:disabled="disabled"
				:required="required"
				:readonly="!editable"
				:aria-labelledby="label ? labelId : undefined"
				:aria-describedby="errorMessage ? errorMessage : undefined"
				:aria-invalid="errorMessage ? 'true' : undefined"
				@click="handleInputClick"
				@dblclick="setToday"
				@blur="handleBlur"
				@input="handleInput"
				@change="handleChange"
				@keydown.space.prevent="openCalendar"
				@keydown.enter.prevent="openCalendar"
			/>


			<span :id="describedByID" v-show="false">
				{{ datePickerInputMessage }}
			</span>

			<div class="input-group-append" style="white-space:nowrap;">
				<button
					type="button"
					class="btn btn-outline-secondary calendar-btn"
					:aria-expanded="isOpen ? 'true' : 'false'"
					aria-haspopup="dialog"
					:aria-label="triggerAriaLabel"
					:disabled="disabled"
					@click="toggleCalendar"
				>
					<i class="fa fa-calendar" aria-hidden="true"></i>
				</button>
			</div>
		</div>

		<!-- Error message -->
		<div
			v-if="errorMessage"
			:id="errorId"
			class="invalid-feedback d-block"
			role="alert"
		>
			{{ errorMessage }}
		</div>

		<!-- Calendar dialog -->
		<div
			v-if="isOpen"
			ref="dialog"
			role="dialog"
			aria-modal="true"
			:aria-labelledby="label ? labelId : 'calendar-dialog'"
			class="calendar-dialog mx-calendar"
			@keydown.esc.prevent="closeCalendar"
			@keydown.tab.capture="trapFocus"
		>
			<div class="calendar-container">
				<wc-datepicker
					ref="wcPicker"
					:disabled="disabled"
					:locale="locale !== 'sa-us' ? locale : 'en-us'"
					:first-day-of-week="firstDayOfWeek"
					:range="range"
					:show-today-button="false"
					:show-clear-button="showClearButton"
					:start-date="startDateIso"
					:value="valueIso"
				/>

				<!-- Today/Now Button (matches old API) -->
				<div class="calendar-actions text-center">
					<br/>
					<button
						type="button"
						class="btn btn-primary today-button"
						@click="setToday"
					>
						{{ todayButtonLabel }}
					</button>
				</div>
			</div>
		</div>

		<!-- Backdrop -->
		<div
			v-if="isOpen"
			class="calendar-backdrop"
			@click="closeCalendar"
		></div>
	</div>
</template>

<script>
	import { defineCustomElements } from 'wc-datepicker/dist/loader';
	import 'wc-datepicker/dist/themes/light.css';
	import moment from 'moment';
	import { ValidationProvider } from 'vee-validate';

	// Initialize the web component
	defineCustomElements();

	export default {
		name: 'AccessabilityDatePicker',
		model: {
			prop: 'value',
			event: 'input'
		},
		props: {
			// Core props to match vue2-datepicker API
			value: {
				type: [Date, Array, String, Object],
				default: null
			},
			name: {
				type: String,
				default: ''
			},
			format: {
				type: String,
				default: 'MM/DD/YYYY'
			},
			editable: {
				type: Boolean,
				default: true
			},
			clearable: {
				type: Boolean,
				default: true
			},
			range: {
				type: Boolean,
				default: false
			},
			rangeSeparator: {
				type: String,
				default: ' ~ '
			},
			// Date range validation
			notBefore: {
				type: [Date, String, Object],
				default: null
			},
			notAfter: {
				type: [Date, String, Object],
				default: null
			},
			disabledDate: {
				type: Function,
				default: null
			},
			// Display props
			placeholder: {
				type: String,
				default: ''
			},
			label: {
				type: String,
				default: ''
			},
			disabled: {
				type: Boolean,
				default: false
			},
			required: {
				type: Boolean,
				default: false
			},
			// Must be set to en for now, not part of the locale code
			lang: {
				type: String,
				default: 'en'
			},
			// Locale props
			locale: {
				type: String,
				default: 'en-US'
			},
			firstDayOfWeek: {
				type: Number,
				default: 0
			},
			// Calendar display
			showTodayButton: {
				type: Boolean,
				default: true
			},
			showClearButton: {
				type: Boolean,
				default: false
			},
			// Validation props
			validateInput: {
				type: Boolean,
				default: false
			},
			validationName: {
				type: String,
				defualt: "input"
			},
			validationInputRules: {
				type: String,
				default: "required|date_format:dd/mm/yyyy"
			},
			// Compatibility props
			errorMessage: {
				type: String,
				default: ''
			},
			// Internal state props
			currentValue: {
				type: [Date, Array, String, Object],
				default: null
			},
			userInput: {
				type: String,
				default: ''
			},
			// Used to hide/show label because the label prop determines what the screen reader will say
			showLabel: {
				type    : Boolean,
				default : true
			},
			// Used to determine when focus is on date input, what screen reader will say
			describedByID: {
				type    : String,
				default : 'id-description-1'
			},
			datePickerInputMessage: {
				type    : String,
				default : 'Date input, date format MM/DD/YYYY'
			}
		},
		data() {
			const uid = Math.random().toString(36).substr(2, 9);

			// The immediate watchers on value/currentValue will handle initial setup.
			return {
				isOpen: false,
				inputId: `date-picker-${uid}`,
				labelId: `date-picker-label-${uid}`,
				errorId: `date-picker-error-${uid}`,
				internalValue: null,
				internalUserInput: this.userInput || ''
			};
		},
		computed: {
			displayValue() {
				if (this.internalUserInput) {
					return this.internalUserInput;
				}

				if (!this.internalValue) return '';

				if (Array.isArray(this.internalValue)) {
					return this.internalValue
						.filter(Boolean)
						.map(d => this.formatDisplayDate(d))
						.join(this.rangeSeparator);
				}

				return this.formatDisplayDate(this.internalValue);
			},
			triggerAriaLabel() {
				if (this.internalValue) {
					return `Change date, currently ${this.displayValue}`;
				}
				return 'Choose date';
			},
			valueIso() {
				return this.toIso(this.internalValue);
			},
			startDateIso() {
				if (this.internalValue && !Array.isArray(this.internalValue)) {
					return this.toIso(this.internalValue);
				}
				return undefined;
			},
			todayButtonLabel() {
				return this.format === 'MM/DD/YYYY' ? 'Today' : 'Now';
			}
		},
		watch: {
			value: {
				handler(newVal) {
					const normalizedValue = this.normalizeValue(newVal);
					if (!this.valuesEqual(normalizedValue, this.internalValue)) {
						this._isUpdatingFromParent = true;
						this.internalValue = normalizedValue;
						this.$nextTick(() => {
							this._isUpdatingFromParent = false;
						});
					}
				},
				immediate: true
			},
			currentValue: {
				handler(newVal) {
					// Only use currentValue if value prop is not set
					if (newVal !== undefined && newVal !== null && !this.value) {
						const normalizedValue = this.normalizeValue(newVal);
						if (!this.valuesEqual(normalizedValue, this.internalValue)) {
							this._isUpdatingFromParent = true;
							this.internalValue = normalizedValue;
							this.$nextTick(() => {
								this._isUpdatingFromParent = false;
							});
						}
					}
				},
				immediate: true
			},
			userInput: {
				handler(newVal) {
					this.internalUserInput = newVal || '';
				},
				immediate: true
			},
			isOpen( opened ) {
				if( opened ) {
					this.$nextTick( () => {
						this.setupPicker();
						// Get the currently focused element
						let focusedElement = document.activeElement;

						// Only set the focus to the first element if the current focus is not the input box
						if( focusedElement.getAttribute( "name" ) !== "selectedDate" ) {
							this.focusFirstElement();
						}
					} );
				}
			}
		},
		methods: {
			// =====================================================
			// CORE: All internal values are stored as moment objects
			// =====================================================

			/**
			 * Normalize any incoming value to a moment object (or null).
			 * This is the single source of truth for type conversion.
			 */
			normalizeValue(value) {
				if (!value) return null;

				if (Array.isArray(value)) {
					return value.map(v => this.normalizeValue(v));
				}

				// Already a moment clone to avoid mutation
				if (moment.isMoment(value)) {
					return value.isValid() ? value.clone() : null;
				}

				// Native Date
				if (value instanceof Date) {
					const m = moment(value);
					return m.isValid() ? m : null;
				}

				// String - try component format first, then fall back to moment's default parsing
				if (typeof value === 'string') {
					// Try with explicit format first (if format is available)
					if (this.format) {
						const parsed = moment(value, this.format);
						if (parsed.isValid()) return parsed;
					}
					// Fallback: let moment try to parse it naturally
					const fallback = moment(value);
					return fallback.isValid() ? fallback : null;
				}

				return null;
			},
			/**
			 * Compare two internal values (moment, array of moments, or null).
			 * Uses day-level granularity.
			 */
			valuesEqual(val1, val2) {
				if (val1 === val2) return true;
				if (!val1 || !val2) return false;

				// Handle arrays (range mode)
				if (Array.isArray(val1) && Array.isArray(val2)) {
					if (val1.length !== val2.length) return false;
					return val1.every((v, i) => this.valuesEqual(v, val2[i]));
				}
				if (Array.isArray(val1) || Array.isArray(val2)) return false;

				// Both should be moment objects at this point
				const m1 = moment.isMoment(val1) ? val1 : moment(val1);
				const m2 = moment.isMoment(val2) ? val2 : moment(val2);

				return m1.isValid() && m2.isValid() && m1.isSame(m2, 'day');
			},
			// =====================================================
			// Input handling
			// =====================================================
			handleInput(event) {
				this.internalUserInput = event.target.value;
				this.$emit('input', this.internalUserInput);
			},
			handleChange(event) {
				let rawValue = event.target.value;
				let formatted = this.formatDate(rawValue);
				let parsed = moment(formatted, this.format);

				if (!parsed.isValid() && rawValue) {
					parsed = moment();
					formatted = parsed.format(this.format);
				}

				// Guard against watcher revert
				this._isUpdatingFromParent = true;

				if (parsed.isValid()) {
					this.internalValue = parsed;
					this.internalUserInput = '';
					this.$emit('input', parsed);
					this.$emit('change', parsed);
				} else {
					this.internalValue = null;
					this.internalUserInput = '';
					this.$emit('input', null);
					this.$emit('change', null);
				}

				this.$nextTick(() => {
					this._isUpdatingFromParent = false;
				});

				if (!moment(rawValue, this.format).isValid() && rawValue) {
					this.$emit('input-error', formatted);
				}
			},
			handleBlur(event) {
				if (this.editable) {
					this.handleChange(event);
				}
				this.$emit('blur', event);
			},
			/**
			 * Attempt to fix partial date strings (e.g. "01/15" "01/15/2026")
			 */
			formatDate(value) {
				let formattedDate = value;

				if (formattedDate && formattedDate.match && formattedDate.match(/\//g)) {
					if (formattedDate.match(/\//g).length === 1) {
						let newYear = moment().year();
						formattedDate = moment(`${formattedDate}/${newYear}`).format(this.format);
					} else {
						formattedDate = moment(formattedDate).format(this.format);
					}
				}

				return formattedDate;
			},
			// =====================================================
			// Emit helpers - always emit moment objects to parent
			// =====================================================

			/**
			 * Central emit method. Always emits moment objects since
			 * the parent always passes moment objects.
			 */
			emitValue(value) {
				if (this._isUpdatingFromParent) return;

				// Ensure we emit moment objects (or null)
				let emitVal = value;
				if (emitVal && !moment.isMoment(emitVal) && !Array.isArray(emitVal)) {
					emitVal = moment(emitVal);
				}
				if (Array.isArray(emitVal)) {
					emitVal = emitVal.map(v => (v && !moment.isMoment(v)) ? moment(v) : v);
				}

				// Guard: prevent the value watcher from reverting internalValue
				// before the parent has time to propagate the new value back
				this._isUpdatingFromParent = true;

				this.$emit('input', emitVal);
				this.$emit('change', emitVal);

				this.$nextTick(() => {
					this._isUpdatingFromParent = false;
				});
			},
			updateDate(emit = false) {
				if (this._isUpdatingFromParent) return;
				if (emit) {
					this.emitValue(this.internalValue);
				}
			},
			parseDate(dateString, format) {
				const parsed = moment(dateString, format || this.format);
				return parsed.isValid() ? parsed : null;
			},
			clearDate() {
				this.internalValue = null;
				this.internalUserInput = '';
				this.$emit('input', null);
				this.$emit('change', null);
				this.$emit('clear');
			},
			closePopup() {
				this.closeCalendar();
			},
			// =====================================================
			// Calendar control
			// =====================================================

			openCalendar() {
				if( this.disabled || this.isOpen ) return;
				this.isOpen = true;
				this.$emit( 'open' );
			},
			closeCalendar() {
				if (!this.isOpen) return;
				this.isOpen = false;
				this.$emit('close');

				this.$nextTick(() => {
					if (this.$refs.input) {
						this.$refs.input.focus();
					}
				});
			},
			toggleCalendar() {
				this.isOpen ? this.closeCalendar() : this.openCalendar();
			},
			handleInputClick() {
				if( this.disabled ) return;

				if( this._clickTimeout ) {
					clearTimeout( this._clickTimeout );
					this._clickTimeout = null;
					return;
				}

				this._clickTimeout = setTimeout( () => {
					this._clickTimeout = null;
					this.openCalendar();
				}, 300 );
			},
			setToday() {
				if (this.disabled) return;

				if (this._clickTimeout) {
					clearTimeout(this._clickTimeout);
					this._clickTimeout = null;
				}

				let dateToSet = moment();

				// If today is disabled, find the best allowed date
				if (this.isDisabledDate(dateToSet.toDate())) {
					dateToSet = this.findBestAllowedDate();

					// If no valid date could be found, just open the calendar
					if (!dateToSet) {
						this.openCalendar();
						return;
					}
				}

				let newValue;
				if (this.range) {
					newValue = [dateToSet, dateToSet.clone()];
				} else {
					newValue = dateToSet;
				}

				// Set flag BEFORE updating internalValue so the value watcher
				// doesn't revert this change when the parent prop hasn't updated yet
				this._isUpdatingFromParent = true;

				this.internalValue = newValue;
				this.internalUserInput = '';

				// Emit to parent
				this.$emit('input', newValue);
				this.$emit('change', newValue);
				this.$emit('set-today', newValue);

				// Release the flag after the parent has had time to update the prop
				this.$nextTick(() => {
					this._isUpdatingFromParent = false;
				});

				// Close the calendar if it's open
				if (this.isOpen) {
					this.$nextTick(() => this.closeCalendar());
				}
			},
			/**
			 * Find the best allowed date when today is disabled.
			 * Priority:
			 *   1. notAfter date (if set and not disabled by disabledDate callback)
			 *   2. notBefore date (if set and not disabled by disabledDate callback)
			 *   3. Search outward from today for the closest allowed date (up to 365 days)
			 * Returns a moment or null if nothing found.
			 */
			findBestAllowedDate() {
				// Try notAfter first (the latest allowed date)
				if (this.notAfter) {
					const afterDate = moment.isMoment(this.notAfter)
						? this.notAfter.clone()
						: moment(this.notAfter);

					if (afterDate.isValid() && !this.isDisabledDate(afterDate.toDate())) {
						return afterDate;
					}
				}

				// Try notBefore (the earliest allowed date)
				if (this.notBefore) {
					const beforeDate = moment.isMoment(this.notBefore)
						? this.notBefore.clone()
						: moment(this.notBefore);

					if (beforeDate.isValid() && !this.isDisabledDate(beforeDate.toDate())) {
						return beforeDate;
					}
				}

				// Search outward from today - check one day before and after, expanding
				const today = moment();
				for (let i = 1; i <= 365; i++) {
					const after = today.clone().add(i, 'days');
					if (!this.isDisabledDate(after.toDate())) {
						return after;
					}

					const before = today.clone().subtract(i, 'days');
					if (!this.isDisabledDate(before.toDate())) {
						return before;
					}
				}

				return null;
			},

			// =====================================================
			// Date validation
			// =====================================================

			isDisabledDate(date) {
				if (this.disabledDate && this.disabledDate(date)) {
					return true;
				}

				if (this.notBefore) {
					const beforeDate = moment.isMoment(this.notBefore)
						? this.notBefore
						: moment(this.notBefore);

					if (moment(date).isBefore(beforeDate, 'day')) {
						return true;
					}
				}

				if (this.notAfter) {
					const afterDate = moment.isMoment(this.notAfter)
						? this.notAfter
						: moment(this.notAfter);

					if (moment(date).isAfter(afterDate, 'day')) {
						return true;
					}
				}

				return false;
			},

			// =====================================================
			// wc-datepicker integration
			// =====================================================

			setupPicker() {
				const picker = this.$refs.wcPicker;
				if( !picker ) return;

				const el = picker.$el || picker;

				el.disableDate = ( date ) => {
					return this.isDisabledDate( date );
				};

				this.attachPickerListeners( el );
			},
			attachPickerListeners(el) {
				if (el._datePickerListenersAttached) return;
				el._datePickerListenersAttached = true;

				this._onSelectDate = (e) => {
					const detail = e.detail;
					let parsed;

					// Convert ISO strings from wc-datepicker to moment objects
					if (Array.isArray(detail)) {
						parsed = detail.map(iso => iso ? moment(iso, 'YYYY-MM-DD') : null);
					} else {
						parsed = detail ? moment(detail, 'YYYY-MM-DD') : null;
					}

					this.internalValue = parsed;
					this.internalUserInput = '';
					this.updateDate(true);

					// Auto-close logic
					if (!this.range && parsed) {
						this.$nextTick(() => this.closeCalendar());
					} else if (this.range && Array.isArray(parsed) && parsed[0] && parsed[1]) {
						this.$nextTick(() => this.closeCalendar());
					}
				};

				el.addEventListener('selectDate', this._onSelectDate);
				this._pickerEl = el;
			},
			removePicker() {
				const el = this._pickerEl;
				if (!el || !this._onSelectDate) return;

				el.removeEventListener('selectDate', this._onSelectDate);
				el._datePickerListenersAttached = false;
			},

			// =====================================================
			// Focus management
			// =====================================================

			focusFirstElement() {
				const dialog = this.$refs.dialog;
				if (!dialog) return;

				const focusable = dialog.querySelector('button:not([disabled]), [href], input:not([disabled])');
				if (focusable) {
					focusable.focus();
				}
			},
			trapFocus(e) {
				const dialog = this.$refs.dialog;
				if (!dialog) return;

				const focusableElements = Array.from(
					dialog.querySelectorAll('button:not([disabled]), [href], input:not([disabled])')
				);

				if (focusableElements.length === 0) return;

				const firstElement = focusableElements[0];
				const lastElement = focusableElements[focusableElements.length - 1];

				if (e.shiftKey) {
					if (document.activeElement === firstElement) {
						e.preventDefault();
						lastElement.focus();
					}
				} else {
					if (document.activeElement === lastElement) {
						e.preventDefault();
						firstElement.focus();
					}
				}
			},

			// =====================================================
			// Date formatting / ISO conversion utilities
			// =====================================================

			/**
			 * Format a moment (or Date/string) for display using this.format.
			 */
			formatDisplayDate(date) {
				if (!date) return '';

				if (moment.isMoment(date)) {
					return date.format(this.format);
				}

				if (typeof date === 'string') {
					return date;
				}

				if (date instanceof Date && !isNaN(date)) {
					return moment(date).format(this.format);
				}

				return '';
			},
			/**
			 * Convert a moment/Date/string to ISO date string (YYYY-MM-DD)
			 * for passing to wc-datepicker.
			 */
			dateToIso(date) {
				if (!date) return undefined;

				if (moment.isMoment(date)) {
					return date.format('YYYY-MM-DD');
				}

				if (typeof date === 'string') {
					const parsed = moment(date, this.format);
					return parsed.isValid() ? parsed.format('YYYY-MM-DD') : undefined;
				}

				if (date instanceof Date && !isNaN(date)) {
					return moment(date).format('YYYY-MM-DD');
				}

				return undefined;
			},
			isoToDate(iso) {
				if (!iso) return null;
				return moment(iso, 'YYYY-MM-DD');
			},
			toIso(value) {
				if (!value) return undefined;
				if (Array.isArray(value)) {
					return value.map(d => this.dateToIso(d));
				}
				return this.dateToIso(value);
			}
		},
		mounted() {
			// Add double-click functionality via jQuery if available, otherwise use native events
			if (window.$ || window.jQuery) {
				const $ = window.$ || window.jQuery;
				const input = this.$refs.input;

				$(input).off('dblclick.datepicker').on('dblclick.datepicker', (e) => {
					e.preventDefault();
					e.stopPropagation();
					this.setToday();
				});
			} else {
				const input = this.$refs.input;
				if (input) {
					input.addEventListener('dblclick', (e) => {
						e.preventDefault();
						e.stopPropagation();
						this.setToday();
					});
				}
			}

			// Close on outside click
			this._onDocClick = (e) => {
				if (this.isOpen && !this.$el.contains(e.target)) {
					this.closeCalendar();
				}
			};
			document.addEventListener('click', this._onDocClick, true);
		},
		beforeDestroy() {
			document.removeEventListener('click', this._onDocClick, true);
			this.removePicker();

			if (window.$ || window.jQuery) {
				const $ = window.$ || window.jQuery;
				$(this.$refs.input).off('dblclick.datepicker');
			}
		},
		components: {
			ValidationProvider
		}
	};
</script>

<style scoped>
	.date-picker-wrapper {
		position: relative;
	}

	.date-picker-label {
		font-weight: 600;
		color: #495057;
		margin-bottom: 0.25rem;
	}

	.required-asterisk {
		color: #dc3545;
		margin-left: 2px;
	}

	.date-input {
		cursor: pointer;
	}

	.date-input:read-only {
		background-color: #fff;
	}

	.calendar-btn {
		border-left: none;
	}

	.calendar-backdrop {
		position: fixed;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		z-index: 1040;
		background: transparent;
	}

	.calendar-dialog {
		position: absolute;
		top: calc(100% + 4px);
		left: 0;
		z-index: 1050;
		background: #fff;
		border: 1px solid #dee2e6;
		border-radius: 0.375rem;
		box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
		min-width: 280px;
		animation: slideIn 0.15s ease-out;
	}

	@keyframes slideIn {
		from {
			opacity: 0;
			transform: translateY(-10px);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}

	.calendar-container {
		padding: 0.75rem;
	}

	.calendar-actions {
		margin-top: 0.75rem;
		padding-top: 0.75rem;
		border-top: 1px solid #dee2e6;
		text-align: center;
	}

	/* Customize wc-datepicker styles to match Bootstrap */
	.calendar-dialog >>> wc-datepicker {
		--wc-datepicker-font-size: 0.875rem;
		--wc-datepicker-background-color: #ffffff;
		--wc-datepicker-border: 1px solid #dee2e6;
		--wc-datepicker-border-radius: 0.25rem;
		--wc-datepicker-highlight-color: #007bff;
		--wc-datepicker-highlight-color-hover: #0056b3;
	}
</style>

<style>
	div.calendar-dialog.mx-calendar > div > wc-datepicker > div.wc-datepicker {
		width: fit-content;
	}
</style>
